// Read-only twin of the entry composer: same ai-grad-border + ai-capture-surface shell,
// same glyph + label + price header — so hovering a placed marker looks exactly like
// placing it. Adds an Edit action instead of the textarea.
import { Pencil } from 'lucide-react'
import { legByKind } from './legTypes'
import type { HoverNote } from './types'

// Narrower than the composer (340): this read-only card only carries a short note
// and a small Edit link — no textarea, no full-width CTA — so 340 read too wide.
const CARD_W = 260

export interface WorkbenchHoverNoteCardProps {
  hover: HoverNote
  wrapW: number
  wrapH: number
  onEdit: (id: string) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  cardRef?: React.Ref<HTMLDivElement>
}

export function WorkbenchHoverNoteCard({
  hover,
  wrapW,
  wrapH,
  onEdit,
  onMouseEnter,
  onMouseLeave,
  cardRef,
}: WorkbenchHoverNoteCardProps) {
  const leg = legByKind(hover.leg)
  const tone =
    leg.tone === 'success' ? 'text-success' : leg.tone === 'danger' ? 'text-danger' : 'text-foreground'
  // Horizontal: centre on the marker, clamped inside the chart so the card never clips an
  // edge. Near the right edge this shifts it left; near the left, right.
  const half = CARD_W / 2
  const PAD = 6
  const GAP = 24
  const cx = wrapW ? Math.min(Math.max(hover.x, half + PAD), wrapW - half - PAD) : hover.x
  // Vertical: flip to the side with more room so the card never sits on the marker. Marker
  // in the TOP half → card BELOW it; BOTTOM half → card ABOVE it. The GAP leaves the marker
  // itself uncovered, so it stays click-and-holdable underneath.
  const placeBelow = wrapH ? hover.y < wrapH / 2 : false
  const cardStyle: React.CSSProperties = placeBelow
    ? { left: cx, top: hover.y + GAP, transform: 'translateX(-50%)' }
    : { left: cx, top: hover.y - GAP, transform: 'translateX(-50%) translateY(-100%)' }

  return (
    <div
      ref={cardRef}
      className="absolute z-[110] animate-composer-in"
      style={{ width: CARD_W, ...cardStyle }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="ai-grad-border">
        <div className="ai-capture-surface p-3.5">
          <div className="flex items-center gap-2 mb-2">
            {leg.glyph && <span className={tone}>{leg.glyph}</span>}
            <span className="text-label text-foreground">{hover.label ?? leg.label}</span>
            <span className="text-caption text-muted-foreground">{hover.price}</span>
          </div>
          {hover.note ? (
            <>
              <div className="text-body text-muted-foreground mb-1.5">{leg.lead}&hellip;</div>
              <div className="text-body text-foreground break-words">{hover.note}</div>
            </>
          ) : (
            <div className="text-body text-muted-foreground/60">No comment</div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => onEdit(hover.id)}
              className="inline-flex items-center gap-1 text-label text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <Pencil size={13} strokeWidth={1.75} /> Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
